'use client';

import Link from 'next/link';
import { ArrowRight, Upload } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import { requestHref, requestHrefGeneric } from '@/lib/request-href';
import type { ReactNode } from 'react';
import { SlashBreaks } from '@/components/seo/SlashBreaks';

/**
 * The one call to action every content page makes: ask licensed pharmacies to
 * quote on this prescription. Carries the drug name into the upload page so
 * the reader does not retype it, and a source tag so we can see which pages
 * produce requests. Two shapes: an inline card and a mobile sticky bar.
 */

function fire(drugName: string, source: string, placement: string) {
  try {
    trackEvent('request_cta_click', { drug: drugName, source, placement });
  } catch {
    // analytics is best-effort
  }
}

export function RequestQuotesInline({ drugName, source, pet = false, compact = false }: { drugName: string; source: string; pet?: boolean; compact?: boolean }) {
  const href = requestHref(drugName, source, pet);
  if (compact) {
    return (
      <Link
        href={href}
        onClick={() => fire(drugName, source, 'hero')}
        className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"
      >
        <Upload className="h-4 w-4" aria-hidden="true" /> Get cash quotes for {drugName}
      </Link>
    );
  }
  return (
    <div className="my-8 flex flex-col gap-4 rounded-2xl border border-primary/20 bg-accent/70 p-5 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <div className="font-semibold text-foreground">Have a {drugName} prescription?</div>
        <p className="mt-1 text-sm text-muted-foreground">
          Upload it and licensed pharmacies reply with their own cash price. You pay the pharmacy, not us.
        </p>
      </div>
      <Link
        href={href}
        onClick={() => fire(drugName, source, 'inline')}
        className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"
      >
        Get quotes <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </div>
  );
}

export function RequestQuotesSticky({ drugName, source, pet = false }: { drugName: string; source: string; pet?: boolean }) {
  const href = requestHref(drugName, source, pet);
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 p-3 backdrop-blur md:hidden" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
      <Link
        href={href}
        onClick={() => fire(drugName, source, 'sticky')}
        className="flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 font-semibold text-primary-foreground shadow-sm"
      >
        <Upload className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0 text-center [overflow-wrap:anywhere]">
          Get cash quotes for <SlashBreaks text={drugName} />
        </span>
      </Link>
    </div>
  );
}

/** A tracked link to the upload page, for templates that build their own buttons. */
export function TrackedQuoteLink({ href, drug, source, placement, className, children }: { href: string; drug: string; source: string; placement: string; className?: string; children: ReactNode }) {
  return (
    <Link href={href} onClick={() => fire(drug, source, placement)} className={className}>
      {children}
    </Link>
  );
}

const HUMAN_BODY = 'Upload it and licensed pharmacies reply with their own cash price. You pay the pharmacy, not us.';
const PET_BODY = 'Many pet medicines are human generics a regular pharmacy can fill with your vet’s prescription. Upload it and licensed pharmacies reply with their own cash price. You pay the pharmacy, not us.';

/** The call to action for pages about a topic rather than one medicine. */
export function RequestQuotesGeneric({ source, pet = false, heading, body, placement = 'generic' }: { source: string; pet?: boolean; heading?: string; body?: string; placement?: string }) {
  const href = requestHrefGeneric(source, pet);
  return (
    <div className="my-8 flex flex-col gap-4 rounded-2xl border border-primary/20 bg-accent/70 p-5 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <div className="font-semibold text-foreground">{heading ?? (pet ? 'Have a prescription for your pet?' : 'Have a prescription?')}</div>
        <p className="mt-1 max-w-[60ch] text-sm text-muted-foreground">{body ?? (pet ? PET_BODY : HUMAN_BODY)}</p>
      </div>
      <Link
        href={href}
        onClick={() => fire('', source, placement)}
        className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"
      >
        Get quotes <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </div>
  );
}

/** Shown instead of a quote button for veterinary-only medicines, which retail pharmacies cannot fill. */
export function VetOnlyNotice({ name }: { name: string }) {
  return (
    <div className="my-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-foreground dark:border-amber-900/50 dark:bg-amber-950/30">
      <div className="font-semibold">Where to get {name}</div>
      <p className="mt-1 max-w-[60ch] text-muted-foreground">
        {name} is a veterinary medicine, dispensed through your vet or a veterinary pharmacy, so it is not something a regular pharmacy can quote on here.{' '}
        <Link href="/pet-prescriptions/human-pharmacy" className="font-medium text-primary underline underline-offset-2">See which pet medicines a regular pharmacy can fill</Link>.
      </p>
    </div>
  );
}
